import React, { createContext, useContext, useReducer, useEffect } from 'react'
import PropTypes from 'prop-types'
import { emailService } from '@/services/emailService'

const AuthContext = createContext()

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
}

const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null }
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      }
    case 'UPDATE_USER':
      return { ...state, user: { ...state.user, ...action.payload } }
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    default:
      return state
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Check for existing token on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('maurifood_token')
      if (token) {
        try {
          // Verify token with backend
          const response = await fetch('/api/auth/verify', {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (response.ok) {
            const userData = await response.json()
            dispatch({ type: 'AUTH_SUCCESS', payload: userData })
          } else {
            localStorage.removeItem('maurifood_token')
            dispatch({ type: 'AUTH_FAILURE', payload: 'Token expired' })
          }
        } catch (error) {
          localStorage.removeItem('maurifood_token')
          dispatch({ type: 'AUTH_FAILURE', payload: 'Authentication failed' })
        }
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: null })
      }
    }

    checkAuthStatus()
  }, [])

  const login = async (credentials) => {
    dispatch({ type: 'AUTH_START' })
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      })
      
      if (response.ok) {
        const { user, token } = await response.json()
        localStorage.setItem('maurifood_token', token)
        dispatch({ type: 'AUTH_SUCCESS', payload: user })
        return { success: true }
      } else {
        const error = await response.json()
        dispatch({ type: 'AUTH_FAILURE', payload: error.message })
        return { success: false, error: error.message }
      }
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: 'Network error' })
      return { success: false, error: 'Network error' }
    }
  }

  const register = async (userData) => {
    dispatch({ type: 'AUTH_START' })
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      })
      
      if (response.ok) {
        const { user, token } = await response.json()
        localStorage.setItem('maurifood_token', token)
        dispatch({ type: 'AUTH_SUCCESS', payload: user })
        
        // Send welcome email after successful registration
        try {
          await emailService.sendWelcomeEmail(user)
          console.log('📧 Welcome email sent to:', user.email)
        } catch (emailError) {
          console.warn('Failed to send welcome email:', emailError)
          // Don't fail registration if email fails
        }
        
        return { success: true }
      } else {
        const error = await response.json()
        dispatch({ type: 'AUTH_FAILURE', payload: error.message })
        return { success: false, error: error.message }
      }
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: 'Network error' })
      return { success: false, error: 'Network error' }
    }
  }

  const logout = () => {
    localStorage.removeItem('maurifood_token')
    dispatch({ type: 'LOGOUT' })
  }

  const updateUser = (updates) => {
    dispatch({ type: 'UPDATE_USER', payload: updates })
  }

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const forgotPassword = async (email) => {
    try {
      // In a real app, this would call your backend API
      // For now, we'll simulate the API call and send the email
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      
      if (response.ok) {
        // Send password reset email
        await emailService.sendPasswordResetEmail({ email, firstName: 'User' })
        return { success: true, message: 'Password reset email sent' }
      } else {
        const error = await response.json()
        return { success: false, error: error.message }
      }
    } catch (error) {
      // Even if API fails, try to send email (for development)
      try {
        await emailService.sendPasswordResetEmail({ email, firstName: 'User' })
        return { success: true, message: 'Password reset email sent' }
      } catch (emailError) {
        return { success: false, error: 'Failed to send password reset email' }
      }
    }
  }

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: newPassword }),
      })
      
      if (response.ok) {
        return { success: true, message: 'Password reset successfully' }
      } else {
        const error = await response.json()
        return { success: false, error: error.message }
      }
    } catch (error) {
      return { success: false, error: 'Network error' }
    }
  }

  const value = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    clearError,
    forgotPassword,
    resetPassword,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useAuth = () => {
  try {
    const context = useContext(AuthContext)
    if (!context) {
      console.warn('useAuth must be used within an AuthProvider')
      // Return a default context to prevent crashes
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        login: () => {},
        logout: () => {},
        register: () => {},
        updateProfile: () => {},
        resetPassword: () => {},
        clearError: () => {}
      }
    }
    return context
  } catch (error) {
    console.error('AuthContext error:', error)
    return {
      user: null,
      isAuthenticated: false,
      isLoading: false,
      login: () => {},
      logout: () => {},
      register: () => {},
      updateProfile: () => {},
      resetPassword: () => {},
      clearError: () => {}
    }
  }
}
